import { ChevronRight } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import { HELP_BASE_PATH, findArticleBySlug } from './helpCenter';
import HelpArticleBadges from './HelpArticleBadges';
import HelpLoadState from './HelpLoadState';
import MarkdownView, { scrollToAnchor } from './MarkdownView';
import { useHelpContent } from './useHelpContent';
import { useHelpArticleVisibility } from './helpVisibility';

const HelpArticlePage = () => {
    const { t } = useTranslation();
    const { section, article } = useParams();
    const location = useLocation();
    const slug = `${section}/${article}`;
    const isVisible = useHelpArticleVisibility();
    const found = findArticleBySlug(slug);
    // An article this user may not see reads as not found, like any unknown one.
    const meta = found && isVisible(found) ? found : undefined;
    const { content, loading, failed, retry } = useHelpContent();
    const bodyRef = useRef<HTMLDivElement>(null);
    const source = content?.[slug];

    // Jump to the anchor once the body is rendered; otherwise start at the top
    // (a new article must not open at the previous one's scroll position).
    useEffect(() => {
        if (!source) return;
        const anchor = decodeURIComponent(location.hash.replace(/^#/, ''));
        if (anchor) scrollToAnchor(anchor, bodyRef.current);
        else window.scrollTo({ top: 0 });
    }, [source, slug, location.hash]);

    if (!meta) {
        return (
            <div className={'max-w-3xl'}>
                <PageHeader>
                    <PageHeadline>{t('help.not_found_title')}</PageHeadline>
                </PageHeader>
                <p className={'text-sm text-foreground/70 mb-4'}>{t('help.not_found_text')}</p>
                <Link className={'text-sm text-primary underline underline-offset-2'} to={HELP_BASE_PATH}>
                    {t('help.back_to_help')}
                </Link>
            </div>
        );
    }

    return (
        <div className={'pb-10'}>
            <nav aria-label={t('help.breadcrumb')} className={'flex items-center gap-1 text-xs text-foreground/50 mb-2'}>
                <Link className={'hover:text-primary'} to={HELP_BASE_PATH}>
                    {t('help.title')}
                </Link>
                <ChevronRight className={'w-3 h-3'} />
                <span>{t(`help.sections.${meta.section}`)}</span>
            </nav>
            <PageHeader>
                <PageHeadline>{meta.title}</PageHeadline>
            </PageHeader>
            <HelpArticleBadges article={meta} />

            <div ref={bodyRef} className={'bg-text-contrast rounded-[5px] shadow-sm p-5 sm:p-8 mt-5'}>
                <HelpLoadState loading={loading} failed={failed} retry={retry} />
                {source !== undefined && <MarkdownView source={source} slug={slug} skipTitle scrollRoot={bodyRef} isVisible={isVisible} />}
            </div>
        </div>
    );
};

export default HelpArticlePage;
